import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import * as config from '@/lib-backend/methods/config';
import { RealmConfig, SiteCacheRule, SiteTemplate } from '@/lib-backend';

/** Outcome of a version pre-flight, normalized for the UI. */
export type ValidateVersionResult =
    | { kind: 'manifest'; rules: SiteCacheRule[]; errors: string[] }
    | { kind: 'no-manifest' }
    | { kind: 'error'; reason: 'NotFound' | 'VersionNotInStorage' | 'Unknown' };

/**
 * Wraps the Admin `config.*siteTemplate*` methods. A SiteTemplate is a pointer
 * to an immutable S3 directory (`{Bucket}/{BasePrefix}/{Version}/...`) — the
 * platform never uploads files; CI does, and the admin only flips pointers.
 * Foundation for both the management page and realm assignment.
 */
@Injectable({ providedIn: 'root' })
export class SiteTemplateService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);
    private readonly notification = inject(NotificationService);

    readonly templates = signal<SiteTemplate[]>([]);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);

    async loadTemplates(): Promise<void> {
        this.loading.set(true);
        this.error.set(null);
        try {
            const resp = await this.api.client.call(config.listSiteTemplates());
            this.templates.set(resp.SiteTemplates);
        } catch (e) {
            this.error.set('Failed to load site templates.');
            handleApiError(e, this.router, this.notification);
        } finally {
            this.loading.set(false);
        }
    }

    async getTemplate(siteTemplateId: string): Promise<SiteTemplate | null> {
        try {
            const resp = await this.api.client.call(config.getSiteTemplate({ SiteTemplateId: siteTemplateId }));
            return resp.Result === 'Ok' ? resp.SiteTemplate : null;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return null;
        }
    }

    async addTemplate(
        params: config.AddSiteTemplateRequest
    ): Promise<{ result: config.AddSiteTemplateResult; template: SiteTemplate | null }> {
        try {
            const resp = await this.api.client.call(config.addSiteTemplate(params));
            if (resp.Result === 'Ok') {
                await this.loadTemplates();
            }
            return { result: resp.Result, template: resp.SiteTemplate };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown', template: null };
        }
    }

    async updateTemplate(
        params: config.UpdateSiteTemplateRequest
    ): Promise<{ result: config.UpdateSiteTemplateResult; template: SiteTemplate | null }> {
        try {
            const resp = await this.api.client.call(config.updateSiteTemplate(params));
            if (resp.Result === 'Ok') {
                await this.loadTemplates();
            }
            return { result: resp.Result, template: resp.SiteTemplate };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown', template: null };
        }
    }

    /** Enumerate version directories actually present in S3 (DB stores only CurrentVersion). */
    async listVersions(
        siteTemplateId: string
    ): Promise<{ result: config.ListSiteTemplateVersionsResult; versions: string[]; currentVersion: string | null }> {
        try {
            const resp = await this.api.client.call(
                config.listSiteTemplateVersions({ SiteTemplateId: siteTemplateId })
            );
            if (resp.Result === 'Ok') {
                return { result: resp.Result, versions: resp.Versions, currentVersion: resp.CurrentVersion };
            }
            return { result: resp.Result, versions: [], currentVersion: null };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown', versions: [], currentVersion: null };
        }
    }

    /**
     * Read-only pre-flight: does the (effective) version resolve to a non-empty
     * storage directory, and what cache rules will its manifest produce? Nulls in
     * the overrides fall back to the template's stored values.
     */
    async validateVersion(params: config.ValidateSiteTemplateVersionRequest): Promise<ValidateVersionResult> {
        try {
            const resp = await this.api.client.call(config.validateSiteTemplateVersion(params));
            switch (resp.Result) {
                case 'OkWithManifest':
                    return { kind: 'manifest', rules: resp.Rules, errors: resp.ManifestErrors };
                case 'OkNoManifest':
                    return { kind: 'no-manifest' };
                case 'NotFound':
                case 'VersionNotInStorage':
                    return { kind: 'error', reason: resp.Result };
                default:
                    return { kind: 'error', reason: 'Unknown' };
            }
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { kind: 'error', reason: 'Unknown' };
        }
    }

    /**
     * Publish: flip CurrentVersion (and optionally relocate bucket/prefix) after
     * validating against storage. Rollback is the same call with an older Version.
     */
    async setSource(
        params: config.SetSiteTemplateSourceRequest
    ): Promise<{ result: config.SetSiteTemplateSourceResult; template: SiteTemplate | null }> {
        try {
            const resp = await this.api.client.call(config.setSiteTemplateSource(params));
            if (resp.Result === 'Ok') {
                await this.loadTemplates();
            }
            return { result: resp.Result, template: resp.SiteTemplate };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown', template: null };
        }
    }

    /** Attach (or, with a null id, detach) a template to a realm. Used by realm assignment (surface B). */
    async setRealmTemplate(
        params: config.SetRealmSiteTemplateRequest
    ): Promise<{ result: config.SetRealmSiteTemplateResult; config: RealmConfig | null }> {
        try {
            const resp = await this.api.client.call(config.setRealmSiteTemplate(params));
            return { result: resp.Result, config: resp.Config };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown', config: null };
        }
    }
}
